import { jsx, jsxs } from "react/jsx-runtime";
import { useColors } from "@inbody/core";
import PlaceIcon from "@mui/icons-material/Place";
import { Box, Typography, Tooltip, Stack } from "@mui/material";
import { formatPinCaption } from "./seatLocation.js";
const RING = 30;
const PIN = 28;
const PIN_TIP = 22 / 24;
const PIN_HEAD = 9 / 24;
const DOT = 9;
function SeatNameLabel({
  children,
  sx,
  color
}) {
  return /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        position: "absolute",
        display: "inline-flex",
        alignItems: "center",
        px: "4px",
        borderRadius: "3px",
        whiteSpace: "nowrap",
        pointerEvents: "none",
        ...sx
      },
      children: /* @__PURE__ */ jsx(
        Typography,
        {
          variant: "small",
          component: "span",
          sx: { lineHeight: 1, fontWeight: 600, color },
          children
        }
      )
    }
  );
}
function SeatFloorPlanView({
  planUrl,
  pins = [],
  highlight,
  showNames = false
}) {
  const { gray, brand } = useColors();
  if (!planUrl) {
    return /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          py: 4,
          display: "flex",
          justifyContent: "center",
          border: `1px dashed ${gray[300]}`,
          borderRadius: 1.5,
          bgcolor: gray[25]
        },
        children: /* @__PURE__ */ jsx(Typography, { variant: "content", sx: { color: gray[500] }, children: "등록된 도면이 없습니다." })
      }
    );
  }
  const others = pins.filter(
    (pin) => !highlight.memberGuid || pin.memberGuid !== highlight.memberGuid
  );
  const labelOnLeft = highlight.x > 80;
  return /* @__PURE__ */ jsxs(
    Box,
    {
      sx: {
        position: "relative",
        border: `1px solid ${gray[200]}`,
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: gray[25]
      },
      children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            component: "img",
            src: planUrl,
            alt: "층 도면",
            draggable: false,
            sx: { display: "block", width: "100%" }
          }
        ),
        others.map((pin) => /* @__PURE__ */ jsx(
          Tooltip,
          {
            title: /* @__PURE__ */ jsxs(Stack, { spacing: 0.25, children: [
              /* @__PURE__ */ jsx(Typography, { variant: "caption", sx: { fontWeight: 700 }, children: pin.fullName || "이름 없음" }),
              formatPinCaption(pin) && /* @__PURE__ */ jsx(Typography, { variant: "small", children: formatPinCaption(pin) })
            ] }),
            placement: "top",
            arrow: true,
            children: /* @__PURE__ */ jsx(
              Box,
              {
                sx: {
                  position: "absolute",
                  left: `${pin.x}%`,
                  top: `${pin.y}%`,
                  transform: "translate(-50%, -50%)",
                  width: DOT,
                  height: DOT,
                  borderRadius: "50%",
                  bgcolor: gray[400],
                  border: `1px solid ${gray[25]}`,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
                  cursor: "default"
                }
              }
            )
          },
          pin.pinId
        )),
        showNames && others.map((pin) => /* @__PURE__ */ jsx(
          SeatNameLabel,
          {
            color: gray[600],
            sx: {
              left: `${pin.x}%`,
              top: `${pin.y}%`,
              transform: "translate(-50%, 5px)",
              height: 16,
              px: "5px",
              borderRadius: "8px",
              bgcolor: gray[25],
              border: `1px solid ${gray[200]}`
            },
            children: pin.fullName || "이름 없음"
          },
          `${pin.pinId}-name`
        )),
        /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              position: "absolute",
              left: `${highlight.x}%`,
              top: `${highlight.y}%`,
              transform: "translate(-50%, -50%)",
              width: RING,
              height: RING,
              borderRadius: "50%",
              border: `2px solid ${brand[400]}`,
              bgcolor: `${brand[100]}66`,
              pointerEvents: "none"
            }
          }
        ),
        /* @__PURE__ */ jsxs(
          Box,
          {
            sx: {
              position: "absolute",
              left: `${highlight.x}%`,
              top: `${highlight.y}%`,
              // 핀 꼭짓점이 좌석 좌표(=고리 중심)에 오도록 꼭짓점 높이만큼 끌어올린다.
              transform: `translate(-50%, -${PIN_TIP * 100}%)`,
              width: PIN,
              height: PIN,
              // 이름 카드가 다른 자리 점에 가리지 않게 위로 올린다.
              zIndex: 1,
              pointerEvents: "none"
            },
            children: [
              /* @__PURE__ */ jsx(
                PlaceIcon,
                {
                  sx: {
                    // inline 요소로 두면 글꼴 베이스라인만큼 아래 여백이 생겨 핀이 떠 보인다.
                    display: "block",
                    fontSize: PIN,
                    color: brand[600],
                    // 도면 선 위에서도 핀이 묻히지 않게 흰 테두리처럼 그림자를 준다.
                    filter: `drop-shadow(0 0 1px ${gray[25]}) drop-shadow(0 1px 2px rgba(0,0,0,0.3))`
                  }
                }
              ),
              /* @__PURE__ */ jsx(
                SeatNameLabel,
                {
                  color: gray[900],
                  sx: {
                    // 핀 머리(동그란 부분) 높이에 맞춰 세로 가운데를 잡는다.
                    top: PIN * PIN_HEAD,
                    transform: "translateY(-50%)",
                    // 아이콘 좌우에 뷰박스 여백이 있어, 그만큼 당겨야 간격이 눈에 맞는다.
                    ...labelOnLeft ? { right: "100%", mr: "-2px" } : { left: "100%", ml: "-2px" },
                    height: 20,
                    px: "7px",
                    borderRadius: "6px",
                    bgcolor: gray[25],
                    boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                  },
                  children: highlight.name
                }
              )
            ]
          }
        )
      ]
    }
  );
}
export {
  SeatFloorPlanView
};
